// chat-tabs.svelte.ts — open-chat tabs + split view (SAY-1820).
//
// Tabs are the chats the user has open across the top of the chat area, like
// a browser: visiting /s/<id> adds the chat as a tab (or activates it), the ×
// closes it, "+" starts a new chat. The sidebar keeps the full history; tabs
// are just the working set. `split` is a second chat shown side by side with
// the active one on wide screens. Persisted per browser in localStorage.

import { browser } from '$app/environment';

const STORAGE_KEY = 'sayano.chatTabs';
export const TABS_MAX = 12;

const tabs = $state<string[]>([]);
let split = $state<string | null>(null);

function persist(): void {
	if (!browser) return;
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify({ tabs, split }));
	} catch {
		/* tab-local only */
	}
}

export function loadTabs(): void {
	if (!browser) return;
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		const parsed = raw ? (JSON.parse(raw) as { tabs?: unknown; split?: unknown }) : {};
		tabs.length = 0;
		if (Array.isArray(parsed.tabs)) tabs.push(...parsed.tabs.filter((x): x is string => typeof x === 'string'));
		split = typeof parsed.split === 'string' ? parsed.split : null;
	} catch {
		tabs.length = 0;
		split = null;
	}
}

export function getTabs(): string[] {
	return tabs;
}

export function getSplit(): string | null {
	return split;
}

/** Ensure a chat has a tab (appended at the end; oldest dropped past TABS_MAX). */
export function openTab(sessionId: string): void {
	if (tabs.includes(sessionId)) return;
	tabs.push(sessionId);
	while (tabs.length > TABS_MAX) tabs.shift();
	persist();
}

/** Close a tab. Returns the neighbour to show next (or null = new chat). */
export function closeTab(sessionId: string): string | null {
	const i = tabs.indexOf(sessionId);
	if (i < 0) return null;
	tabs.splice(i, 1);
	if (split === sessionId) split = null;
	persist();
	return tabs[i] ?? tabs[i - 1] ?? null;
}

/** Drop a chat from tabs/split without choosing a successor (deleted/trashed). */
export function forgetTab(sessionId: string): void {
	const i = tabs.indexOf(sessionId);
	if (i >= 0) tabs.splice(i, 1);
	if (split === sessionId) split = null;
	persist();
}

export function setSplit(sessionId: string | null): void {
	split = sessionId;
	persist();
}
